import { applyLoan } from "../API/API";
import axios from "../API/Axios";
import { store } from "../ReduxStore/Store";
import { selectFiles, selectFormData } from "../ReduxStore/UserSlice";
import { base64ToFile } from "../components/common/Util/Util";

import originalAxios from "axios";
import { TOKEN_KEYS } from "../components/common/Util/Util";
import { USER_TYPES, BORROWER_TYPES, ENTITY_TYPES } from "../utils/constants";

const TEMP_LOGOUT_TOKEN = "temp_logout_token";

let isSessionTimeoutSaveInProgress = false;

let lastSaveRequestTime = 0;
const SAVE_DEBOUNCE_MS = 2000;
let pendingSavePromise = null;
let lastSaveTimestamp = null;

let isEmergencySaveInProgress = false;

class DraftApplicationService {
  static markSessionTimeoutCall(isTimeout) {
    if (isTimeout && isSessionTimeoutSaveInProgress) {
      return;
    }
    isSessionTimeoutSaveInProgress = isTimeout;
    localStorage.setItem(
      "sessionTimeoutInProgress",
      isTimeout ? "true" : "false"
    );
  }

  /**
   * @param {string} token
   * @returns {string|null}
   */
  static cleanToken(token) {
    if (!token) {
      return null;
    }

    try {
      let cleanedToken = token.trim();

      while (cleanedToken.toLowerCase().startsWith("bearer ")) {
        cleanedToken = cleanedToken.substring(7).trim();
      }

      if (!cleanedToken) {
        console.warn("[DRAFT-SERVICE] Token is empty after cleaning");
        return null;
      }

      const formattedToken = `Bearer ${cleanedToken}`;

      return formattedToken;
    } catch (error) {
      console.error("[DRAFT-SERVICE] Error cleaning token:", error);
      return null;
    }
  }

  static getTokenForTimeout() {
    if (
      !isSessionTimeoutSaveInProgress &&
      localStorage.getItem("sessionTimeoutInProgress") !== "true"
    ) {
      return localStorage.getItem(TOKEN_KEYS.JWT);
    }

    if (
      isSessionTimeoutSaveInProgress ||
      localStorage.getItem("sessionTimeoutInProgress") === "true"
    ) {
      const cachedToken = localStorage.getItem(TEMP_LOGOUT_TOKEN);

      if (cachedToken) {
        return cachedToken;
      }
    }

    const token = localStorage.getItem(TOKEN_KEYS.JWT);

    if (!token) {
    } else if (localStorage.getItem("sessionTimeoutInProgress") === "true") {
      localStorage.setItem(TEMP_LOGOUT_TOKEN, token);
    }

    return token;
  }

  /**
   * @returns {Object}
   */
  static getAuthHeaders() {
    const token = DraftApplicationService.getTokenForTimeout();
    const headers = {};

    if (token) {
      const cleanToken = DraftApplicationService.cleanToken(token);
      if (cleanToken) {
        headers["Authorization"] = cleanToken;
      }
    }

    return headers;
  }

  /**
   * @returns {Object}
   */
  static createSessionTimeoutAxios() {
    const baseURL = axios.defaults.baseURL;

    const instance = originalAxios.create({
      baseURL,
      withCredentials: true,
    });

    instance.interceptors.request.use((config) => {
      const isSessionTimeout =
        localStorage.getItem("sessionTimeoutInProgress") === "true";

      if (isSessionTimeout) {
        const token =
          localStorage.getItem(TEMP_LOGOUT_TOKEN) ||
          localStorage.getItem(TOKEN_KEYS.JWT);

        if (token) {
          if (config.headers && config.headers.Authorization) {
            delete config.headers.Authorization;
          }
          config.headers.Authorization =
            DraftApplicationService.cleanToken(token);
        }
      } else {
        const regularToken = localStorage.getItem(TOKEN_KEYS.JWT);
        if (regularToken && !config.headers.Authorization) {
          config.headers.Authorization =
            DraftApplicationService.cleanToken(regularToken);
        }
      }

      return config;
    });

    return instance;
  }

  /**
   * @param {Object} customData
   * @returns {Promise}
   */
  static async saveDraft(customData = null) {
    const isSessionTimeout =
      localStorage.getItem("sessionTimeoutInProgress") === "true";

    if (isSessionTimeout) {
      const now = Date.now();
      const eventTimestamp = customData?.timestamp || now;

      if (pendingSavePromise) {
        return pendingSavePromise;
      }

      if (lastSaveTimestamp && eventTimestamp <= lastSaveTimestamp) {
        return Promise.resolve({
          success: true,
          skipped: true,
          statusCode: 200,
        });
      }

      if (now - lastSaveRequestTime < SAVE_DEBOUNCE_MS) {
        return Promise.resolve({
          success: true,
          skipped: true,
          statusCode: 200,
        });
      }

      if (isEmergencySaveInProgress) {
        return Promise.resolve({
          success: true,
          skipped: true,
          statusCode: 200,
        });
      }

      lastSaveRequestTime = now;
      lastSaveTimestamp = eventTimestamp;
    }

    if (isSessionTimeout) {
      try {
        pendingSavePromise =
          DraftApplicationService.emergencyDirectSave(customData);
        const result = await pendingSavePromise;
        pendingSavePromise = null;
        return result;
      } catch (emergencyError) {
        console.error(
          "[DRAFT-SERVICE] Emergency direct save failed:",
          emergencyError
        );
        pendingSavePromise = null;
      }
    }

    if (isSessionTimeout) {
      DraftApplicationService.markSessionTimeoutCall(true);
    }

    try {
      const state = store.getState();
      const formData = customData || selectFormData(state);
      const files = selectFiles(state);
      const user = state.user?.user || {};
      const userId =
        user.userId ||
        user.id ||
        user.ID ||
        formData.userId ||
        (typeof localStorage !== "undefined" &&
          localStorage.getItem("userId")) ||
        null;

      if (!userId) {
        throw new Error("userId is required for saving draft application");
      }

      const data = new FormData();

      const formDetails = {
        ...formData,
        userId: userId,
        userType: USER_TYPES.BORROWER,
        borrowerType: user.BorrowerType || user.borrowerType || BORROWER_TYPES.INDIVIDUAL,
        status: "incomplete",
      };

      if (formDetails.borrowerType.toLowerCase() === BORROWER_TYPES.ENTITY.toLowerCase()) {
        const entityType = (
          user.EntityType || 
          user.entityType || 
          formData.entityType || 
          localStorage.getItem('userEntityType') || 
          ''
        ).toLowerCase();

        switch(entityType) {
          case ENTITY_TYPES.COMPANY.toLowerCase():
            formDetails.entityType = ENTITY_TYPES.COMPANY;
            break;
          case ENTITY_TYPES.PARTNERSHIP_FIRM.toLowerCase():
          case 'partnership_firm':
          case 'partnership':
            formDetails.entityType = ENTITY_TYPES.PARTNERSHIP_FIRM;
            break;
          case ENTITY_TYPES.propreitorship_firm.toLowerCase():
          case 'propreitorship_firm':
          case 'proprietorship':
            formDetails.entityType = ENTITY_TYPES.propreitorship_firm;
            break;
          default:
            formDetails.entityType = entityType;
        }
      }

      data.append("userId", userId);
      data.append("status", "incomplete");

      Object.entries(formDetails).forEach(([key, value]) => {
        if (
          key !== "userId" &&
          key !== "status" &&
          value !== undefined &&
          value !== null
        ) {
          data.append(key, value);
        }
      });
      const processedFields = new Set();
      Object.entries(files).forEach(([key, value]) => {
        if (processedFields.has(key)) return;

        if (Array.isArray(value)) {
          processedFields.add(key);

          if (
            key === "applicantItr" ||
            key === "coApplicantItr" ||
            key === "bankStatement" ||
            key === "slLoan" ||
            key === "salarySlips" ||
            key === "itr" ||
            key === "balanceSheet"
          ) {
            const filteredValue = value.filter((item) => item !== null);

            filteredValue.forEach((file, index) => {
              if (file) {
                try {
                  if (file.base64 && file.filename) {
                    const fileObj = base64ToFile(file.base64, file.filename);
                    if (fileObj) {
                      data.append(key, fileObj, fileObj.name);
                    } else {
                      console.error(
                        `[DRAFT-SERVICE] Failed to convert base64 to file for ${key}[${index}]`
                      );
                    }
                  } else if (file instanceof File) {
                    data.append(key, file, file.name);
                  } else {
                    console.warn(
                      `[DRAFT-SERVICE] File for ${key}[${index}] is neither base64 nor File object:`,
                      typeof file
                    );
                  }
                } catch (error) {
                  console.error(
                    `[DRAFT-SERVICE] Error processing file for ${key}[${index}]:`,
                    error
                  );
                }
              }
            });
          } else {
            if (key === "AadharCard") {
              processedFields.add("aadharCard");
            } else if (key === "aadharCard") {
              processedFields.add("AadharCard");
            }

            value.forEach((file, index) => {
              if (file) {
                try {
                  if (file.base64 && file.filename) {
                    const fileObj = base64ToFile(file.base64, file.filename);
                    if (fileObj) {
                      data.append(key, fileObj, fileObj.name);
                    } else {
                      console.error(
                        `[DRAFT-SERVICE] Failed to convert base64 to file for ${key}[${index}]`
                      );
                    }
                  } else if (file instanceof File) {
                    data.append(key, file, file.name);
                  } else {
                    console.warn(
                      `[DRAFT-SERVICE] File for ${key}[${index}] is neither base64 nor File object:`,
                      typeof file
                    );
                  }
                } catch (error) {
                  console.error(
                    `[DRAFT-SERVICE] Error processing file for ${key}[${index}]:`,
                    error
                  );
                }
              }
            });
          }
        } else if (value) {
          processedFields.add(key);
          try {
            if (value.base64 && value.filename) {
              const fileObj = base64ToFile(value.base64, value.filename);
              if (fileObj) {
                data.append(key, fileObj, fileObj.name);
              } else {
                console.error(
                  `[DRAFT-SERVICE] Failed to convert base64 to file for ${key}`
                );
              }
            } else if (value instanceof File) {
              data.append(key, value, value.name);
            } else {
              console.warn(
                `[DRAFT-SERVICE] File for ${key} is neither base64 nor File object:`,
                typeof value
              );
            }
          } catch (error) {
            console.error(
              `[DRAFT-SERVICE] Error processing file for ${key}:`,
              error
            );
          }
        }
      });

      try {
        let response;

        if (isSessionTimeout) {
          const sessionTimeoutAxios =
            DraftApplicationService.createSessionTimeoutAxios();
          const apiResponse = await sessionTimeoutAxios.post(
            "/api/loan/apply",
            data
          );
          response = apiResponse.data;
        } else {
          response = await applyLoan(data);
        }
        // Verify response structure
        if (!response) {
          console.error("[DRAFT-SERVICE] API response is empty or undefined");
        } else if (response.statusCode !== 200) {
          console.error(
            "[DRAFT-SERVICE] API returned non-200 status code:",
            response.statusCode
          );
        }

        return response;
      } catch (apiError) {
        console.error("[DRAFT-SERVICE] API call error:", apiError);
        console.error("[DRAFT-SERVICE] API call stack:", apiError.stack);
        if (apiError.response) {
          console.error(
            "[DRAFT-SERVICE] API error response:",
            apiError.response.data
          );
          console.error(
            "[DRAFT-SERVICE] API error status:",
            apiError.response.status
          );
        }
        throw apiError;
      } finally {
        if (isSessionTimeout) {
          // Reset the session timeout call flag
          DraftApplicationService.markSessionTimeoutCall(false);
        }
      }
    } catch (error) {
      console.error("[DRAFT-SERVICE] Error in saveDraft function:", error);
      console.error("[DRAFT-SERVICE] Error stack:", error.stack);

      // Reset the session timeout call flag if needed
      if (isSessionTimeout) {
        DraftApplicationService.markSessionTimeoutCall(false);
      }

      throw error;
    }
  }

  static async emergencyDirectSave(customData = null) {
    if (isEmergencySaveInProgress) {
      return {
        statusCode: 200,
        message: "Emergency save already in progress",
        success: true,
        debounced: true,
      };
    }

    try {
      isEmergencySaveInProgress = true;

      const immediateTokenCapture = localStorage.getItem(TOKEN_KEYS.JWT);
      if (immediateTokenCapture) {
        localStorage.setItem(TEMP_LOGOUT_TOKEN, immediateTokenCapture);
      }

      const state = store.getState();
      const formData = customData || selectFormData(state);
      const files = selectFiles(state);
      const user = state.user?.user || {};

      const authToken =
        immediateTokenCapture ||
        localStorage.getItem(TOKEN_KEYS.JWT) ||
        user?.jwtToken ||
        localStorage.getItem(TEMP_LOGOUT_TOKEN);

      let domBasedToken = null;
      try {
        const tokenElement = document.getElementById("auth-token");
        if (tokenElement) {
          domBasedToken =
            tokenElement.value || tokenElement.getAttribute("data-token");
        }

        if (!domBasedToken && document.cookie) {
          const cookieMatch = document.cookie.match(/jwtToken=([^;]+)/);
          if (cookieMatch) {
            domBasedToken = cookieMatch[1];
          }
        }
      } catch (domError) {
        console.error(
          "[DRAFT-SERVICE] EMERGENCY: Error in DOM-based token retrieval:",
          domError
        );
      }

      const finalAuthToken = authToken || domBasedToken;

      if (finalAuthToken && !localStorage.getItem(TEMP_LOGOUT_TOKEN)) {
        localStorage.setItem(TEMP_LOGOUT_TOKEN, finalAuthToken);
      }

      if (!finalAuthToken) {
        console.error(
          "[DRAFT-SERVICE] EMERGENCY: No auth token available for direct API call. Attempted all retrieval methods."
        );
        throw new Error("No auth token available");
      }

      const userId =
        user.userId ||
        user.id ||
        user.ID ||
        formData.userId ||
        localStorage.getItem("userId") ||
        null;

      if (!userId) {
        console.error(
          "[DRAFT-SERVICE] EMERGENCY: No userId available for direct API call"
        );
        throw new Error("userId is required");
      }

      const data = new FormData();

      const entityType =
        user.entityType ||
        user.EntityType ||
        formData.entityType ||
        localStorage.getItem(TOKEN_KEYS.ENTITY_TYPE) ||
        (user.userType?.toLowerCase() === BORROWER_TYPES.ENTITY ? BORROWER_TYPES.ENTITY : BORROWER_TYPES.INDIVIDUAL);

      const borrowerType =
        user.borrowerType ||
        user.BorrowerType ||
        formData.borrowerType ||
        localStorage.getItem(TOKEN_KEYS.BUSINESS_TYPE) ||
        (entityType === BORROWER_TYPES.ENTITY ? ENTITY_TYPES.COMPANY : BORROWER_TYPES.INDIVIDUAL);

      const formDetails = {
        ...formData,
        userId: userId,
        userType: USER_TYPES.BORROWER,
        borrowerType: user.BorrowerType || user.borrowerType || BORROWER_TYPES.INDIVIDUAL,
        status: "incomplete",
      };

      if (formDetails.borrowerType === BORROWER_TYPES.ENTITY) {
        formDetails.entityType = ENTITY_TYPES.COMPANY;
      }

      data.append("userId", userId);

      Object.entries(formDetails).forEach(([key, value]) => {
        if (key !== "userId" && value !== undefined && value !== null) {
          data.append(key, value);
        }
      });

      const processedFields = new Set();

      Object.entries(files).forEach(([key, value]) => {
        if (processedFields.has(key)) return;

        if (Array.isArray(value)) {
          processedFields.add(key);

          if (
            key === "applicantItr" ||
            key === "coApplicantItr" ||
            key === "bankStatement" ||
            key === "slLoan" ||
            key === "salarySlips" ||
            key === "itr" ||
            key === "balanceSheet"
          ) {
            const filteredValue = value.filter((item) => item !== null);

            filteredValue.forEach((file, index) => {
              if (file) {
                try {
                  if (file.base64 && file.filename) {
                    const fileObj = base64ToFile(file.base64, file.filename);
                    if (fileObj) {
                      data.append(key, fileObj, fileObj.name);
                    } else {
                      console.error(
                        `Failed to convert base64 to file for ${key}[${index}]`
                      );
                    }
                  } else if (file instanceof File) {
                    data.append(key, file, file.name);
                  }
                } catch (error) {
                  console.error(
                    `Error processing file for ${key}[${index}]:`,
                    error
                  );
                }
              }
            });
          } else {
            if (key === "AadharCard") {
              processedFields.add("aadharCard");
            } else if (key === "aadharCard") {
              processedFields.add("AadharCard");
            }

            value.forEach((file, index) => {
              if (file) {
                try {
                  if (file.base64 && file.filename) {
                    const fileObj = base64ToFile(file.base64, file.filename);
                    if (fileObj) {
                      data.append(key, fileObj, fileObj.name);
                    } else {
                      console.error(
                        `Failed to convert base64 to file for ${key}[${index}]`
                      );
                    }
                  } else if (file instanceof File) {
                    data.append(key, file, file.name);
                  }
                } catch (error) {
                  console.error(
                    `Error processing file for ${key}[${index}]:`,
                    error
                  );
                }
              }
            });
          }
        } else if (value) {
          processedFields.add(key);

          try {
            if (value.base64 && value.filename) {
              const fileObj = base64ToFile(value.base64, value.filename);
              if (fileObj) {
                data.append(key, fileObj, fileObj.name);
              } else {
                console.error(`Failed to convert base64 to file for ${key}`);
              }
            } else if (value instanceof File) {
              data.append(key, value, value.name);
            }
          } catch (error) {
            console.error(`Error processing file for ${key}:`, error);
          }
        }
      });

      const cleanAuthToken = DraftApplicationService.cleanToken(finalAuthToken);
      if (!cleanAuthToken) {
        console.error(
          "[DRAFT-SERVICE] EMERGENCY: Token cleaning failed - invalid token structure"
        );
        throw new Error("Invalid token structure");
      }

      const baseURL = axios.defaults.baseURL;
      const API_URL = `${baseURL}/api/loan/apply`;
      const ULTRA_SHORT_TIMEOUT = 2000;

      let responseReceived = false;

      const sessionTimeoutAxios =
        DraftApplicationService.createSessionTimeoutAxios();

      try {
        const fetchHeaders = {
          Authorization: cleanAuthToken,
          Accept: "application/json",
        };

        const fetchResponse = await fetch(API_URL, {
          method: "POST",
          headers: fetchHeaders,
          body: data,
          credentials: "include",
          signal: AbortSignal.timeout(ULTRA_SHORT_TIMEOUT),
        });

        if (fetchResponse.ok) {
          responseReceived = true;
          return await fetchResponse.json();
        } else {
          throw new Error(`Fetch failed with status ${fetchResponse.status}`);
        }
      } catch (fetchError) {
        console.error(
          "[DRAFT-SERVICE] EMERGENCY: Fetch API approach failed:",
          fetchError
        );

        if (!responseReceived) {
          try {
            const axiosConfig = {
              headers: {
                Authorization: cleanAuthToken,
                "Content-Type": "multipart/form-data",
                Accept: "application/json",
              },
              timeout: ULTRA_SHORT_TIMEOUT,
              withCredentials: true,
            };

            const axiosResponse = await sessionTimeoutAxios.post(
              "/api/loan/apply",
              data,
              axiosConfig
            );
            responseReceived = true;
            return axiosResponse.data;
          } catch (specialAxiosError) {
            console.error(
              "[DRAFT-SERVICE] EMERGENCY: Special axios approach failed:",
              specialAxiosError
            );
          }
        }

        if (!responseReceived) {
          try {
            const xhr = new XMLHttpRequest();
            const xhrResult = await new Promise((resolve, reject) => {
              xhr.open("POST", API_URL, true);
              xhr.setRequestHeader("Authorization", cleanAuthToken);
              xhr.setRequestHeader("Accept", "application/json");
              xhr.withCredentials = true;
              xhr.timeout = ULTRA_SHORT_TIMEOUT;

              xhr.onload = function () {
                if (xhr.status >= 200 && xhr.status < 300) {
                  try {
                    resolve(JSON.parse(xhr.responseText));
                  } catch (e) {
                    resolve({ success: true, statusCode: xhr.status });
                  }
                } else {
                  reject(new Error(`XHR failed with status ${xhr.status}`));
                }
              };

              xhr.ontimeout = function () {
                reject(new Error("XHR request timed out"));
              };

              xhr.onerror = function () {
                reject(new Error("XHR network error"));
              };

              xhr.send(data);
            });

            responseReceived = true;
            return xhrResult;
          } catch (xhrError) {
            console.error(
              "[DRAFT-SERVICE] EMERGENCY: XHR approach failed:",
              xhrError
            );
          }
        }

        if (!responseReceived) {
          const axiosHeaders = {
            Authorization: cleanAuthToken,
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          };

          try {
            const directResponse = await originalAxios.post(API_URL, data, {
              headers: axiosHeaders,
              timeout: ULTRA_SHORT_TIMEOUT,
              withCredentials: true,
            });

            return directResponse.data;
          } catch (directAxiosError) {
            throw directAxiosError;
          }
        }
      }

      throw new Error("All API call approaches failed");
    } catch (error) {
      console.error(
        "[DRAFT-SERVICE] EMERGENCY: Direct API call failed:",
        error
      );
      throw error;
    } finally {
      isEmergencySaveInProgress = false;
      pendingSavePromise = null;
    }
  }

  /**
   * @param {Object} formData
   * @returns {boolean}
   */
  static shouldOfferDraftSave(formData) {
    if (!formData) return false;

    const hasEssentialFields = Boolean(
      formData.mobileNo ||
        formData.email ||
        formData.profession ||
        formData.panNumber ||
        formData.gstin ||
        formData.nameAsPerGST ||
        formData.fundRequirement ||
        formData.endUseOfFund ||
        formData.securityOffered ||
        formData.addr ||
        formData.udyamRegistrationNo
    );

    return hasEssentialFields;
  }
}

export default DraftApplicationService;
